import { createRef, Component } from "react";
import { webix } from "extensions/webix";
import isPlainObject from "lodash-es/isPlainObject";

import { nextTick } from "common/utils";

import { WebixDatatable } from "common/components/webix/react/Datatable";
import { TableControls } from "common/components/configTable/tableControls";
import { Paginator } from "common/components/paginator/paginator";
import { ErrorView } from "common/components/error";
import { TablePlaceholder } from "common/components/placeholders";
import {
    ErrorFetchingAdminReport,
    ErrorReportNoEntries
} from "common/helpers/errors";

import { columnsMapper } from "./helpers";

export class ReportGrid extends Component {
    static defaultProps = {
        hasDetails: false,
        hasCheckbox: false,
        hasRadio: false,
        hidePager: true,
        single: false
    };

    static getHeight = () => Math.max(300, window.innerHeight - 414);

    constructor(props) {
        super(props);
        this.webixTableRef = createRef();
        this.state = {
            fullView: false
        };
    }

    componentDidMount() {
        window.addEventListener("resize", this.updateTableDimensions);
    }

    componentWillUnmount() {
        if (this.ttPopup) {
            this.ttPopup.destructor();
        }
        window.removeEventListener("resize", this.updateTableDimensions);
    }

    getHiddenColumns() {
        return this.props.meta?.grid_display_overrides?.filter(({ column_hidden }) => column_hidden).map(({ column_value }) => column_value) || [];
    }

    updateTableDimensions = () => {
        if (this.webixTableRef.current) {
            this.webixTableRef.current.table.current.ui.define("height", ReportGrid.getHeight());
        }
    };

    getCachedReportFilter = () => {
        if (typeof this.props.getCachedReportValues !== "undefined") {
            return this.props.getCachedReportValues(this.props.meta?._id)?.gridFilter;
        }
    };

    cacheReportFilter = (newFilter) => {
        if (typeof this.props.cacheReportValues !== "undefined") {
            this.props.cacheReportValues(this.props.meta._id, { gridFilter: newFilter });
        }
    };

    showPopUp(column, content, offset) {
        if (!this.ttPopup) {
            this.renderTooltipPopup();
        }

        let str;
        let dataType;
        if (Array.isArray(content)) {
            str = content.map((i) => JSON.stringify(i)).join("<br>");
            dataType = "array";
        } else if (typeof content === "string") {
            str = content;
            dataType = "string";
        } else {
            str = JSON.stringify(content, null, "\t");
            dataType = "object";
        }

        const pop = this.ttPopup;
        const bdy = pop.getBody();
        bdy.setHTML(`<div id="tooltip" class="ttPopup column-${column} dataType-${dataType}">
                        <div class="ttPopupHeader">${column}</div>
                        <div class="ttPopupContent">${str}</div>
                     </div>`);
        pop.show({
            x: offset.x,
            y: offset.y
        });
    }

    createColumnDefinitions() {
        // eslint-disable-next-line eqeqeq
        if (this.props.data.results[0] == null) return [];

        let columns = Object.entries(this.props.frankenResult).map(columnsMapper({
            single: this.props.single,
            walk: this.props.walk,
            cacheReportFilter: this.cacheReportFilter,
            overrides: this.props.meta?.grid_display_overrides,
            canonicalName: this.props.meta?.canonical_name
        }));

        if (!this.state.fullView) {
            const hidden = this.getHiddenColumns();
            if (hidden.length) {
                columns = columns.filter(({ id }) => !hidden.includes(id));
            }
        }

        if (this.props.hasDetails) {
            columns = [{
                id: "_id",
                header: "",
                width: 38,
                css: "details-link",
                sort: false,
                template: (item) => "<span><i class='fal fa-info-square open-details'></i></span>"
            }, ...columns];
        }

        if (this.props.hasCheckbox) {
            columns = [{
                id: "ch1",
                header: { content: "masterCheckbox", tooltip: () => `${this.props.numSelectedRows === this.props.data.count ? "Deselect" : "Select"} all` },
                width: 40,
                template: "{common.checkbox()}"
            }, ...columns];
        }

        if (this.props.hasRadio) {
            columns = [{
                id: "ch1",
                header: "",
                width: 38,
                template: "{common.radio()}"
            }, ...columns];
        }
        const overriddenColumns = this.overrideColumnsWithMetaData(columns);
        return overriddenColumns;
    }

    overrideColumnsWithMetaData(columns) {
        const overrides = this.props.meta?.grid_display_overrides;
        if (!overrides?.length) {
            return columns;
        }
        const cols = columns.map((column) => {
            const columnOverride = overrides.find(
                (override) => override.column_value === column.id
            );
            if (!columnOverride?.column_alias) return column;
            if (!Array.isArray(column.header)) {
                // The info column has an id of "_id"
                // but should still have an empty header when "_id" gets a new label
                if (column.id !== "_id") column.header = columnOverride.column_alias;
            } else {
                column.header[0].inputConfig.label = columnOverride.column_alias;
            }
            return column;
        });
        return cols;
    }

    findIdColumn(row) {
        if (this.props.single) {
            return this.props.itemId;
        }

        if (this.props.itemId || (Array.isArray(this.props.walk) && this.props.walk.length)) return void 0;

        return row._id || row.op_incident_ref;
    }

    renderPager() {
        if (this.props.hidePager
            || !this.props.data
            || !Array.isArray(this.props.data.results)
            || this.props.data.results.length === 0) {
            return null;
        }

        const { page, limit, count, next } = this.props.data;

        return (
            <Paginator
                page={page}
                count={count}
                next={next}
                loading={this.props.loading}
                limit={limit}
                onPageChange={this.props.handlePageClick}
                results_length={this.props.data.results.length}
            />
        );
    }

    renderTooltipPopup() {
        this.ttPopup = webix.ui({
            view: "popup",
            id: "ttPop",
            css: "admin-report-popup",
            body: "Testing content",
            width: 750,
            height: 300
        });
    }

    renderTabularDataView() {
        if (this.props.loading) return <TablePlaceholder columns={5} rows={10} />;

        if (!this.props.data) return null;

        if (this.props.data.results?.length === 0) {
            return (
                <ErrorView
                    error={new ErrorReportNoEntries({
                        header: "Report query returned no records",
                        message: "Try removing any applied filters or select another report",
                        actions: false,
                        hideDetails: true
                    })}
                    captureInSentry={false}
                    custom
                />
            );
        }

        const indexedData = this.props.data.results.map((r, index) => ({
            ...r,
            _lookupIndex: index
        }));

        const self = this;
        const ui = {
            minHeight: 330,
            height: ReportGrid.getHeight(),
            scrollX: true,
            borderless: false,
            select: "row",
            multiselect: true,
            leftSplit: this.props.walk ? 0 : 1,
            scroll: true,
            columns: this.createColumnDefinitions(),
            on: {
                onCheck(row, column, state) {
                    const selectedRows = new Set();

                    // Not sure if there is a reduce method on datatable rows
                    this.eachRow((id) => {
                        const item = this.getItem(id);
                        if (item.ch1) {
                            selectedRows.add(item._id);
                        }
                    });

                    self.props.eventHandler({
                        type: "CHECKBOX_CHANGE",
                        data: Array.from(selectedRows)
                    });
                },

                onItemClick({ row, column }, e, obj) {
                    // Prevent default browser action, e.g. clicking an anchor
                    e.preventDefault();

                    const item = this.getItem(row);
                    nextTick(() => {
                        // the canonical name length check is used to disable a clickable id in actions
                        // there's additional handling in helpers.columnsMapper
                        const inAction = self.props.meta.canonical_name?.split("__").length === 4;
                        if (column === "_id" && !inAction) {
                            if (self.props.itemId) {
                                self.props.reportDisplayArray({
                                    itemId: void 0,
                                    index: item._lookupIndex,
                                    column: "_id"
                                });
                            } else {
                                // Calling with only itemId registers as edit.
                                self.props.reportDisplayArray({
                                    itemId: self.findIdColumn(item)
                                });
                            }
                        } else {
                            const data = item[column];
                            if ((Array.isArray(data) && data.length) || isPlainObject(data)) {
                                // We should use original index, not the index from the sorted collection.
                                self.props.reportDisplayArray({
                                    itemId: self.findIdColumn(item),
                                    index: column === "value" ? void 0 : item._lookupIndex,
                                    column: column === "value" ? item.property : column
                                });
                            } else if (data && data.length > 26) { // 26 is ISO date string length
                                self.showPopUp(column, data, {
                                    x: e.offsetX,
                                    y: e.offsetY
                                });
                            }
                        }
                    });

                    return false;
                },
                onAfterLoad() {
                    if (this.count()) {
                        this.eachRow((row) => {
                            const { priority = "regular" } = this.getItem(row);
                            this.addRowCss(row, `priority-${priority}`);
                        });
                    }
                    const cachedFilter = self.getCachedReportFilter();
                    if (cachedFilter) {
                        Object.keys(cachedFilter).forEach((key) => {
                            this.filter(key, `${cachedFilter[key]}`, true);
                        });
                        if (this.count() === 0) {
                            // This occurs when changes to an item
                            // in the detail view remove it from the report
                            // and we've filtered down to that item.
                            this.filter(Object.keys(cachedFilter)[0], "");
                            self.cacheReportFilter(void 0);
                        }
                    }
                }
            },

            ready() {
                this.eachColumn(function(id) {
                    const { width, maxWidth } = this.getColumnConfig(id);
                    if (maxWidth && width > maxWidth) {
                        this.setColumnWidth(id, maxWidth);
                    }
                });

                if (self.props.selectRow) {
                    const [lastRowData] = this.data.find(({ _id }) => _id === self.props.selectRow);
                    if (lastRowData?.id) {
                        this.select(lastRowData.id);
                        this.showItem(lastRowData.id);
                    }
                }
            },

            tooltip(obj, common, e) {
                const val = obj[common.column.id];

                if (Array.isArray(val)) {
                    return "Click cell for full details.";
                }

                return webix.template.escape(val);
            }

        };

        return (
            <WebixDatatable
                ref={this.webixTableRef}
                className="report-grid"
                ui={ui}
                data={indexedData}
            />
        );
    }

    render() {
        if (this.props.error.has_error) {
            return (
                <ErrorView
                    error={new ErrorFetchingAdminReport({
                        message: "There was an error fetching this report",
                        originalError: this.props.error,
                        hideDetails: true,
                        tags: {
                            status: this.props.error?.status,
                            statusText: this.props.error?.statusText
                        }
                    })}
                />
            );
        }

        return (
            <>
                <TableControls
                    expanded={this.state.fullView}
                    toggleView={() => this.setState((prevState) => ({ fullView: !prevState.fullView }))}
                    paginatorTop
                    loading={this.props.loading}
                    showExportButton={false}
                    showConfigButton={false}
                    showCollapsedToggle={this.getHiddenColumns().length > 0}
                    paginator={this.renderPager()}
                />
                {this.renderTabularDataView()}
                {this.renderPager()}
            </>
        );
    }
}
